// Focus chips: scope · subject ✕ · week. Every chip writes to the FocusContext, so it survives a
// mode switch. SETUP-REGROUP (review #19; shell design §2.2): they show only on screens that read
// them (`focusControlsAt`): the week on KPIs' pages that read it, Reports and a DE's profile, the
// scope on the org chart and the scorecard; Chat, Work, Home, Setup and the rest show none, since a control there did
// nothing. The focus itself is kept while hidden. The scope chip is back with M1-08: Chart's
// TEAMS tree sets the scope and the chart filters by it, and the chip picks it in every mode
// (Company or a team). Its team names come from data the chart already loaded (the org chart or
// the teams list in the cache), so the chip never fetches; a team it has no name for yet shows
// as "Team …id" and can still be cleared.
import { PeriodPicker, ScopeChip, SubjectChip, workerName } from "@companyos/ui";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { formatSubject, recentWeeks, type Subject, useFocus } from "../focus/focus";
import { weekRangeLabel } from "../focus/weeks";

const SUBJECT_LABEL = { seat: "Seat", person: "Person", task: "Task", de: "DE", team: "Team" };

const weekOption = (key: string) => ({
  value: key,
  label: `W${key.slice(-2)} · ${weekRangeLabel(key)}`,
});

/**
 * KPIs pages that never read the focused week: Priorities (a quarter), MEETINGS' Issues,
 * Meetings (a series list, or a meeting with its own date) and Vision, and MEMBER-ENTRY's My
 * numbers (each number has its own period picker). My scorecard reads it (its week grain).
 */
const KPIS_WITHOUT_WEEK = /^(priorities|issues|meetings(\/.*)?|vision|my-numbers|chart\/editor)$/;

export type FocusControls = { scope: boolean; week?: boolean };

/**
 * Which focus controls the screen at `pathname` uses, or null for none: the week (with the
 * subject chip) on the KPIs pages that read it (not `KPIS_WITHOUT_WEEK`), Reports (Finance too)
 * and a DE's profile; the scope on the org chart and the scorecard, the screens it filters. The
 * org chart has its own period control by lens (KPIS-MODE: shown only on the lenses that read a
 * period), so the top bar shows no week there (`week: false`).
 */
export function focusControlsAt(
  pathname: string,
  search: Record<string, unknown> = {},
): FocusControls | null {
  const [, c, , mode, ...rest] = pathname.replace(/\/+$/, "").split("/");
  if (c !== "c") return null;
  const page = rest.join("/");
  if (mode === "kpis") {
    // UX-A: My numbers' My scorecard tab reads the week (its week grain), as its own page did.
    if (page === "my-numbers" && search.tab === "scorecard") return { scope: false };
    if (page === "" || KPIS_WITHOUT_WEEK.test(page)) return null;
    if (page === "chart") return { scope: true, week: false };
    if (page === "scorecard") return { scope: true };
    // KPIs › Reports (NAV-REDESIGN) and the rest: the week, no scope.
    return { scope: false };
  }
  if (mode === "agents" && rest[0] === "de" && rest[1]) return { scope: false };
  return null;
}

export function FocusChips({
  today = new Date(),
  scope: showScope = true,
  week: showWeek = true,
}: {
  today?: Date;
  /** The scope chip (Company or a team): the org chart and the scorecard filter by it. */
  scope?: boolean;
  /** The week picker (KPIS-MODE: not on the org chart, whose period control is by lens). */
  week?: boolean;
}) {
  const { focus, setFocus } = useFocus();
  const weeks = useMemo(() => recentWeeks(today), [today]);
  const current = focus.period ?? weeks[0] ?? "";
  const options = (weeks.includes(current) ? weeks : [current, ...weeks]).map(weekOption);
  const subjectName = useSubjectName(focus.subject);
  const scopes = useScopeOptions(focus.company.id, focus.scope);

  return (
    <>
      {/* Phones keep the week in view: the company scope is the default and is not shown there
          (Chart's TEAMS tree, behind the menu, sets a team). */}
      {showScope ? (
        <ScopeChip
          value={focus.scope}
          options={scopes}
          onChange={(scope) => setFocus({ scope })}
          {...(focus.scope === "company" ? { className: "max-sm:hidden" } : {})}
        />
      ) : null}
      {focus.subject ? (
        // The name comes from data a screen already loaded (no extra call); the chip truncates
        // and the title keeps the full id.
        <SubjectChip
          kind={SUBJECT_LABEL[focus.subject.kind]}
          name={subjectName}
          title={`${SUBJECT_LABEL[focus.subject.kind]}: ${focus.subject.id}`}
          clearLabel={`Clear focus ${formatSubject(focus.subject)}`}
          onClear={() => setFocus({ subject: null })}
        />
      ) : null}
      {showWeek ? (
        <PeriodPicker value={current} weeks={options} onChange={(p) => setFocus({ period: p })} />
      ) : null}
    </>
  );
}

/**
 * The scopes the chip offers: Company, then every team in the cache (A–Z), with the current
 * scope always present. Read from the query cache, so the chip costs no call.
 */
export function useScopeOptions(
  companyId: string,
  scope: string,
): { value: string; label: string }[] {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient],
  );
  const snapshot = () => JSON.stringify(cachedTeams(queryClient, companyId));
  const teams = useSyncExternalStore(subscribe, snapshot, snapshot);
  return useMemo(() => {
    const list = JSON.parse(teams) as [string, string][];
    const options = [
      { value: "company", label: "Company" },
      ...list.map(([id, name]) => ({ value: `team:${id}`, label: `Team: ${name}` })),
    ];
    if (!options.some((o) => o.value === scope))
      options.push({
        value: scope,
        label: scope.startsWith("team:") ? `Team …${scope.slice(-6)}` : scope,
      });
    return options;
  }, [teams, scope]);
}

/** `[id, name]` of the company's teams in the cache (org chart, or the teams list), A–Z. */
function cachedTeams(queryClient: QueryClient, companyId: string): [string, string][] {
  type Named = { id: string; name: string };
  const out = new Map<string, string>();
  const chart = queryClient.getQueryData<{ teams?: Named[] }>(["company", companyId, "org-chart"]);
  for (const t of chart?.teams ?? []) out.set(t.id, t.name);
  const list = queryClient.getQueryData<{ pages?: { data?: Named[] }[] }>([
    "company",
    companyId,
    "teams",
  ]);
  for (const p of list?.pages ?? []) for (const t of p.data ?? []) out.set(t.id, t.name);
  return [...out].sort((a, b) => a[1].localeCompare(b[1]));
}

/**
 * A readable name for the focused subject, looked up in data the screens already loaded (a DE
 * profile or list row, a seat): the chip never fetches. Falls back to a short id.
 */
export function useSubjectName(subject: Subject | null): string {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient],
  );
  const snapshot = () => (subject ? findName(queryClient, subject) : "");
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

function findName(queryClient: QueryClient, subject: Subject): string {
  for (const query of queryClient.getQueryCache().getAll()) {
    const name = search(query.state.data, subject, 0);
    if (name) return name;
  }
  return `…${subject.id.slice(-6)}`;
}

function search(value: unknown, subject: Subject, depth: number): string | null {
  if (depth > 6 || value === null || typeof value !== "object") return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const name = search(item, subject, depth + 1);
      if (name) return name;
    }
    return null;
  }
  const row = value as Record<string, unknown>;
  if (subject.kind === "de" && row.membership_id === subject.id) {
    if (typeof row.display_name === "string") return workerName(row.display_name);
  }
  if (subject.kind === "seat" && row.id === subject.id && typeof row.title === "string")
    return row.title;
  if (subject.kind === "team" && row.id === subject.id && typeof row.name === "string")
    return row.name;
  if (subject.kind === "person" && row.membership_id === subject.id) {
    if (typeof row.name === "string") return row.name;
  }
  for (const v of Object.values(row)) {
    const name = search(v, subject, depth + 1);
    if (name) return name;
  }
  return null;
}
